import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface BlockedTime {
  id: string;
  blocked_date: string;
  start_time: string;
  end_time: string;
  reason: string | null;
}

export const BlockedTimeManager = () => {
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchBlockedTimes();
  }, []);

  const fetchBlockedTimes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("blocked_times")
        .select("*")
        .eq("coach_id", user.id)
        .order("blocked_date", { ascending: true })
        .order("start_time", { ascending: true });

      if (error) throw error;
      setBlockedTimes(data || []);
    } catch (error) {
      console.error("Error fetching blocked times:", error);
      toast.error("Failed to load blocked times");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlockedTime = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !startTime || !endTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.from("blocked_times").insert({
        coach_id: user.id,
        blocked_date: format(selectedDate, "yyyy-MM-dd"),
        start_time: startTime,
        end_time: endTime,
        reason: reason || null,
      });

      if (error) throw error;

      toast.success("Time blocked successfully");
      setSelectedDate(undefined);
      setStartTime("");
      setEndTime("");
      setReason("");
      setIsDialogOpen(false);
      fetchBlockedTimes();
    } catch (error) {
      console.error("Error adding blocked time:", error);
      toast.error("Failed to block time");
    }
  };

  const handleDeleteBlockedTime = async (id: string) => {
    try {
      const { error } = await supabase
        .from("blocked_times")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Blocked time removed");
      fetchBlockedTimes();
    } catch (error) {
      console.error("Error deleting blocked time:", error);
      toast.error("Failed to remove blocked time");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Blocked Time Slots
            </CardTitle>
            <CardDescription>
              Block time when you're unavailable for lessons
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Block Time
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Block Time Off</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddBlockedTime} className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-md border"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-time">Start Time</Label>
                    <Input
                      id="start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-time">End Time</Label>
                    <Input
                      id="end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason (Optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="e.g., Personal appointment, Holiday, etc."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full">
                  Block Time
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center text-muted-foreground py-4">Loading...</p>
        ) : blockedTimes.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No blocked times. Click "Block Time" to add unavailable slots.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {blockedTimes.map((blockedTime) => (
                <TableRow key={blockedTime.id}>
                  <TableCell className="font-medium">
                    {format(new Date(blockedTime.blocked_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {blockedTime.start_time} - {blockedTime.end_time}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {blockedTime.reason || "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBlockedTime(blockedTime.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
